import React, { useState, useEffect, useCallback, useRef } from 'react';
import AreaWorkItemBoqTable from './AreaWorkItemBoqTable';
import BoqReadOnlyView from './BoqReadOnlyView';
import axiosInstance from '../../services/api/axios';
import { toast } from 'react-toastify';
import { PrimaryButton, OutlineButton } from '../../components/Buttons';
import { Save, Loader2, Lock } from 'lucide-react';

// Map a material line from GET /interior-designers/id-boq/grouped into the editor row shape.
// v3_58: a BOQ row is just a free-text material + quantity + unit of measure (no category chain,
// no SKU / price / credits).
function mapMaterialItemToRow(item) {
    const boqId = item.interiorDesignerBoqId ?? item.id ?? null;
    const qty = item.itemQuantity;
    return {
        id: boqId ?? (Date.now() + Math.floor(Math.random() * 1000)),
        interiorDesignerBoqId: boqId,
        materialName: item.materialName ?? '',
        quantity: qty != null ? String(qty) : '',
        unit: item.itemUnit ?? '',
        notes: item.notes ?? '',
    };
}

const blankRow = () => ({
    id: Date.now() + Math.floor(Math.random() * 1000),
    interiorDesignerBoqId: null,
    materialName: '',
    quantity: '',
    unit: '',
    notes: '',
});

const blankUnitArea = () => ({
    id: Date.now() + Math.floor(Math.random() * 1000),
    boqUnitAreaId: null,
    areaName: '',
    isCollapsed: false,
    workItems: [
        {
            id: Date.now() + Math.floor(Math.random() * 1000) + 1,
            boqWorkItemId: null,
            workItemName: '',
            isCollapsed: false,
            items: [blankRow()],
        },
    ],
});

function BoqContainer({
    designSubmissionId,
    designIterationId,
    leadId,
    portfolioId,
    onSuccess,
    onCancel,
    isReadOnly = false,
    // Atomic-save plumbing — the parent (e.g. DesignSubmissionModal) can defer creating the parent
    // resource (design iteration) until the BOQ Save click. See the original contract.
    onPreSave,
    onSaveError,
    saveButtonLabel = 'Save BOQ',
}) {
    const boqScopeId = designIterationId ?? designSubmissionId ?? (portfolioId ? `portfolio_${portfolioId}` : null);
    const [unitAreas, setUnitAreas] = useState([blankUnitArea()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Self-detect iteration approval lock → collapse to the read-only view.
    const [iterationLocked, setIterationLocked] = useState(false);
    const [iterationLockReason, setIterationLockReason] = useState('');
    const [iterationLockChecked, setIterationLockChecked] = useState(false);
    useEffect(() => {
        if (!designIterationId) {
            setIterationLocked(false);
            setIterationLockChecked(true);
            return undefined;
        }
        let cancelled = false;
        setIterationLockChecked(false);
        (async () => {
            try {
                // `designSubmissionId` is the lead id now — resolve the deliverable directly.
                const res = await axiosInstance.get(`/interior-designers/design-deliverables`, {
                    params: { id_lead_id: designSubmissionId },
                });
                const body = res?.data ?? res;
                const items = Array.isArray(body) ? body : Array.isArray(body?.items) ? body.items : [];
                const me = items.find(
                    (it) => Number(it?.designIterationId ?? it?.design_iteration_id) === Number(designIterationId)
                );
                if (cancelled || !me) {
                    if (!cancelled) setIterationLockChecked(true);
                    return;
                }
                const status = (me.status ?? '').toString().toUpperCase();
                if (status === 'APPROVED') {
                    setIterationLocked(true);
                    setIterationLockReason('This design has been approved. The BOQ is locked.');
                } else {
                    setIterationLocked(false);
                    setIterationLockReason('');
                }
            } catch (e) {
                if (!cancelled) { setIterationLocked(false); setIterationLockReason(''); }
            } finally {
                if (!cancelled) setIterationLockChecked(true);
            }
        })();
        return () => { cancelled = true; };
    }, [designIterationId, designSubmissionId]);
    const effectiveReadOnly = isReadOnly || iterationLocked;

    // Track ids deleted from the tree so we can call DELETE during save.
    const deletedUnitAreaIdsRef = useRef(new Set());
    const deletedWorkItemIdsRef = useRef(new Set());
    const deletedMaterialIdsRef = useRef(new Set());
    const trackDeletedUnitArea = useCallback((id) => { if (id) deletedUnitAreaIdsRef.current.add(id); }, []);
    const trackDeletedWorkItem = useCallback((id) => { if (id) deletedWorkItemIdsRef.current.add(id); }, []);
    const trackDeletedMaterial = useCallback((id) => { if (id) deletedMaterialIdsRef.current.add(id); }, []);

    const fetchBoqItems = useCallback(async () => {
        setIsLoading(true);
        deletedUnitAreaIdsRef.current = new Set();
        deletedWorkItemIdsRef.current = new Set();
        deletedMaterialIdsRef.current = new Set();
        try {
            const params = new URLSearchParams();
            if (designIterationId) params.append('design_iteration_id', designIterationId);
            else if (portfolioId) params.append('portfolio_item_id', portfolioId);
            else { setUnitAreas([blankUnitArea()]); setIsLoading(false); return; }

            const response = await axiosInstance.get(`/interior-designers/id-boq/grouped?${params.toString()}`);
            const raw = response?.data ?? response;
            const responseUnitAreas = Array.isArray(raw?.unitAreas) ? raw.unitAreas
                : Array.isArray(raw?.data?.unitAreas) ? raw.data.unitAreas : [];
            const ungroupedMaterials = Array.isArray(raw?.ungroupedMaterials) ? raw.ungroupedMaterials
                : Array.isArray(raw?.data?.ungroupedMaterials) ? raw.data.ungroupedMaterials : [];

            const emptyWi = (gIdx, wIdx) => ({
                id: Date.now() + gIdx * 1000 + wIdx, boqWorkItemId: null, workItemName: '',
                isCollapsed: false, items: [blankRow()],
            });

            const built = responseUnitAreas.map((group, gIdx) => {
                const ua = group?.unitArea ?? group?.unit_area ?? {};
                const wis = Array.isArray(group?.workItems) ? group.workItems
                    : Array.isArray(group?.work_items) ? group.work_items : [];
                return {
                    id: ua.boqUnitAreaId ?? ua.boq_unit_area_id ?? Date.now() + gIdx,
                    boqUnitAreaId: ua.boqUnitAreaId ?? ua.boq_unit_area_id ?? null,
                    areaName: ua.areaName ?? ua.area_name ?? '',
                    isCollapsed: false,
                    workItems: wis.length
                        ? wis.map((wig, wIdx) => {
                            const wi = wig?.workItem ?? wig?.work_item ?? {};
                            const materials = Array.isArray(wig?.materials) ? wig.materials : [];
                            return {
                                id: wi.boqWorkItemId ?? wi.boq_work_item_id ?? Date.now() + gIdx * 1000 + wIdx,
                                boqWorkItemId: wi.boqWorkItemId ?? wi.boq_work_item_id ?? null,
                                workItemName: wi.workItemName ?? wi.work_item_name ?? '',
                                isCollapsed: false,
                                items: materials.length ? materials.map(mapMaterialItemToRow) : [blankRow()],
                            };
                        })
                        : [emptyWi(gIdx, 0)],
                };
            });

            if (ungroupedMaterials.length > 0) {
                built.push({
                    id: Date.now() + 99999, boqUnitAreaId: null, areaName: 'Ungrouped', isCollapsed: false,
                    workItems: [{
                        id: Date.now() + 99999 + 1, boqWorkItemId: null, workItemName: 'Ungrouped',
                        isCollapsed: false,
                        items: ungroupedMaterials.map(mapMaterialItemToRow),
                    }],
                });
            }

            setUnitAreas(built.length ? built : [blankUnitArea()]);
        } catch (error) {
            console.error('Error fetching BOQ:', error);
            setUnitAreas([blankUnitArea()]);
        } finally {
            setIsLoading(false);
        }
    }, [designIterationId, portfolioId]);

    useEffect(() => {
        if (boqScopeId || portfolioId) fetchBoqItems();
    }, [boqScopeId, portfolioId, fetchBoqItems]);

    const handleSave = async () => {
        let resolvedSubmissionId = designSubmissionId;
        let resolvedIterationId = designIterationId;
        let preSavedInThisCall = false;
        if (typeof onPreSave === 'function') {
            try {
                const ids = await onPreSave();
                if (ids && (ids.designSubmissionId || ids.designIterationId)) {
                    resolvedSubmissionId = ids.designSubmissionId ?? resolvedSubmissionId;
                    resolvedIterationId = ids.designIterationId ?? resolvedIterationId;
                    preSavedInThisCall = true;
                }
            } catch (preErr) {
                toast.error(preErr?.response?.data?.detail || preErr?.message || 'Failed to save design details — BOQ was not saved.');
                return;
            }
        }

        const parsedIterationId = Number(resolvedIterationId);
        const parsedPortfolioId = Number(portfolioId);
        const validIterationId = Number.isFinite(parsedIterationId) && parsedIterationId > 0 ? parsedIterationId : null;
        const validPortfolioId = Number.isFinite(parsedPortfolioId) && parsedPortfolioId > 0 ? parsedPortfolioId : null;
        const hasIterationScope = validIterationId != null;
        const hasPortfolioScope = validPortfolioId != null;
        if (hasIterationScope && hasPortfolioScope) {
            toast.error('BOQ has both design iteration AND portfolio scope — pass exactly one.');
            return;
        }
        if (!hasIterationScope && !hasPortfolioScope) {
            toast.error(
                typeof onPreSave === 'function'
                    ? 'Could not resolve the design iteration to attach this BOQ to. Try again.'
                    : 'No design iteration or portfolio is selected. Save the design submission first, then add BOQ rows.'
            );
            return;
        }

        // Detect partially-filled rows (a row is "touched" if it has a name or a quantity).
        const partial = [];
        unitAreas.forEach((area, areaIdx) => {
            area.workItems.forEach((wi, wiIdx) => {
                wi.items.forEach((item, itemIdx) => {
                    const hasName = Boolean(String(item.materialName ?? '').trim());
                    const hasQty = item.quantity != null && String(item.quantity).trim() !== '' && parseFloat(item.quantity) > 0;
                    if (!hasName && !hasQty) return;
                    if (!hasName) partial.push({ areaIdx, wiIdx, itemIdx, reason: 'material name' });
                    else if (!hasQty) partial.push({ areaIdx, wiIdx, itemIdx, reason: 'quantity' });
                });
            });
        });
        if (partial.length > 0) {
            const f = partial[0];
            toast.error(`${partial.length} BOQ row${partial.length === 1 ? '' : 's'} incomplete — row ${f.itemIdx + 1} (work item ${f.wiIdx + 1}, unit area ${f.areaIdx + 1}) is missing ${f.reason}.`);
            return;
        }

        // Rows with content require an area name + a work item name.
        const missingNames = [];
        unitAreas.forEach((area, areaIdx) => {
            area.workItems.forEach((wi, wiIdx) => {
                const hasContentRow = wi.items.some((it) => String(it.materialName ?? '').trim() && it.quantity);
                if (!hasContentRow) return;
                if (!String(area.areaName ?? '').trim()) missingNames.push({ areaIdx, wiIdx, kind: 'area' });
                if (!String(wi.workItemName ?? '').trim()) missingNames.push({ areaIdx, wiIdx, kind: 'workItem' });
            });
        });
        if (missingNames.length > 0) {
            const m = missingNames[0];
            toast.error(m.kind === 'area'
                ? `Unit area ${m.areaIdx + 1} needs a name (e.g. "Hall").`
                : `Work item ${m.wiIdx + 1} in unit area ${m.areaIdx + 1} needs a name (e.g. "TV Unit").`);
            return;
        }

        // Flatten to material items, carrying area/work-item names for auto-create.
        const allItems = [];
        unitAreas.forEach((area) => {
            const areaName = String(area.areaName ?? '').trim();
            area.workItems.forEach((wi) => {
                const workItemName = String(wi.workItemName ?? '').trim();
                wi.items.forEach((item) => {
                    if (!(String(item.materialName ?? '').trim() && item.quantity)) return;
                    const payloadItem = {
                        designIterationId: hasIterationScope ? validIterationId : null,
                        portfolioItemId: hasPortfolioScope ? validPortfolioId : null,
                        materialName: String(item.materialName).trim(),
                        itemQuantity: parseFloat(item.quantity) || 0,
                        itemUnit: item.unit ? String(item.unit).trim() : null,
                        notes: item.notes || '',
                        unitAreaName: areaName,
                        workItemName,
                    };
                    if (wi.boqWorkItemId) payloadItem.boqWorkItemId = wi.boqWorkItemId;
                    const existingRaw = item.interiorDesignerBoqId;
                    const existingNum = Number(existingRaw);
                    const existingBoqId = existingRaw != null && existingRaw !== '' && Number.isFinite(existingNum) && existingNum > 0 ? existingNum : null;
                    allItems.push({ payload: payloadItem, _existingBoqId: existingBoqId });
                });
            });
        });

        const toCreate = [];
        const toUpdate = [];
        for (const entry of allItems) {
            if (entry._existingBoqId != null) toUpdate.push({ boqId: entry._existingBoqId, body: entry.payload });
            else toCreate.push(entry.payload);
        }

        const deletedMaterialIds = Array.from(deletedMaterialIdsRef.current);
        const deletedWorkItemIds = Array.from(deletedWorkItemIdsRef.current);
        const deletedUnitAreaIds = Array.from(deletedUnitAreaIdsRef.current);

        if (!toCreate.length && !toUpdate.length && !deletedMaterialIds.length && !deletedWorkItemIds.length && !deletedUnitAreaIds.length) {
            toast.warning('Please add at least one item with quantity to the BOQ.');
            return;
        }

        setIsSubmitting(true);
        try {
            for (const id of deletedMaterialIds) {
                try { await axiosInstance.delete(`/interior-designers/id-boq/${id}`); } catch (e) { console.warn('delete material', id, e); }
            }
            for (const id of deletedWorkItemIds) {
                try { await axiosInstance.delete(`/interior-designers/id-boq/work-items/${id}`); } catch (e) { console.warn('delete work item', id, e); }
            }
            for (const id of deletedUnitAreaIds) {
                try { await axiosInstance.delete(`/interior-designers/id-boq/unit-areas/${id}`); } catch (e) { console.warn('delete unit area', id, e); }
            }
            if (toCreate.length > 0) await axiosInstance.post('/interior-designers/id-boq', toCreate);
            for (const { boqId, body } of toUpdate) await axiosInstance.put(`/interior-designers/id-boq/${boqId}`, body);
            await fetchBoqItems();
            toast.success('BOQ saved successfully!');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error saving BOQ:', error);
            toast.error(error.response?.data?.message || 'Failed to save BOQ.');
            if (preSavedInThisCall && typeof onSaveError === 'function') {
                try { await onSaveError({ designSubmissionId: resolvedSubmissionId, designIterationId: resolvedIterationId }); }
                catch (rollbackErr) { console.error('Rollback after BOQ save failure errored:', rollbackErr); }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (iterationLockChecked && iterationLocked) {
        return (
            <div className="flex flex-col h-full gap-3">
                <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 shadow-sm">
                    <Lock size={16} className="mt-0.5 shrink-0 text-amber-700" aria-hidden />
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-900">BOQ locked</p>
                        <p className="text-xs text-amber-800 mt-0.5">{iterationLockReason}</p>
                    </div>
                </div>
                <div className="flex-grow overflow-auto min-h-[320px] rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <BoqReadOnlyView designIterationId={designIterationId} designSubmissionId={designSubmissionId} portfolioId={portfolioId} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full gap-3">
            <div className="flex-grow overflow-auto min-h-[360px] rounded-2xl border border-slate-200 bg-white shadow-sm">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center min-h-[320px] gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-flamingo-50 border border-flamingo-100 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-flamingo-500 animate-spin" strokeWidth={2} />
                        </div>
                        <p className="text-sm text-slate-600 font-medium">Loading BOQ…</p>
                    </div>
                ) : (
                    <AreaWorkItemBoqTable
                        unitAreas={unitAreas}
                        setUnitAreas={setUnitAreas}
                        onTrackDeletedUnitArea={trackDeletedUnitArea}
                        onTrackDeletedWorkItem={trackDeletedWorkItem}
                        onTrackDeletedMaterial={trackDeletedMaterial}
                    />
                )}
            </div>

            {!effectiveReadOnly && (
                <div className="flex items-center justify-end gap-2 py-3 px-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-sm shrink-0">
                    {onCancel && (
                        <OutlineButton onClick={onCancel} className="!w-auto !px-4 !py-2 text-sm">Cancel</OutlineButton>
                    )}
                    <PrimaryButton onClick={handleSave} disabled={isSubmitting} className="flex items-center gap-2 !w-auto !px-4 !py-2 text-sm">
                        {isSubmitting ? (<><Loader2 size={16} className="animate-spin" /> Saving…</>) : (<><Save size={16} /> {saveButtonLabel}</>)}
                    </PrimaryButton>
                </div>
            )}
        </div>
    );
}

export default BoqContainer;
